import React from "react";
import { css, useTheme } from "@emotion/react";

import ArrowBox from "core/Components/Icons/ArrowBox";
import { useAppState } from "core/contexts/AppContext";
import { localeCurrency } from "core/includes/formatters";
import ContentCombine from "core/Components/ContentCombine";
import { taxLabelWithTaxable } from "core/includes/finance";
import { DEFAULT_COUNTRY_CODE_CONFIG } from "core/includes/constants";

type Props = {
    className?: string,
    price: number,
    discountPrice: number,
    shouldDiscount: boolean,
    isTaxable: boolean,
    isTaxUnderPrice?: boolean,
};

const ItemPriceBox: React.FC<Props> = ({
    className = '',
    price,
    discountPrice = 0,
    shouldDiscount = false,
    isTaxable,
    isTaxUnderPrice = false,
}) => {
    const [appState] = useAppState();
    const theme = useTheme();

    const currency = appState.globalCurrencyOptions.isGlobal
        ? appState.globalCurrencyOptions.currencySymbol
        : appState.countryCode.currency || DEFAULT_COUNTRY_CODE_CONFIG.currency;

    const containerStyle = css`
        display: grid;
        grid-template-columns: 1fr max-content 1fr;

        position: relative;
        justify-content: center;
        margin-top: 43px;

        ${theme.breakpoints.between('lg', 'xl')} {
            margin-top: 55px;
        }

        ${theme.breakpoints.only('md')} {
            margin-top: 28px;
        }

        ${theme.breakpoints.only('sm')} {
            margin-top: 30px;
        }

        ${theme.breakpoints.down('xs')} {
            margin-top: 33px;
        }
    `;

    const relativeContainerStyle = css`
        position: relative;
    `;

    const saleIcon = css`
        position: absolute;
        top: -15px;
        right: 1rem;
        width: 121px;
        justify-self: flex-end;

        ${theme.breakpoints.only('xl')} {
            top: -18px;
        }

        ${theme.breakpoints.between('md', 'lg')} {
            top: -23px;
        }
        
        ${theme.breakpoints.down('sm')} {
            top: -10px;
            width: 87px;
        }
    `;

    const slashedPriceStyle = css`
        position: absolute;
        left: 0.2rem;
        top: -25px;

        font-size: 27px;
        font-weight: ${theme.fonts.weights.light};
        text-decoration: line-through ${theme.colours.red1};

        ${theme.breakpoints.between('md', 'xl')} {
            font-size: 21px;
            top: -22px;
        }

        ${theme.breakpoints.down('sm')} {
            font-size: 15px;
            top: -15px;
        }
    `;

    const priceStyle = css`
        font-size: 50px;

        ${theme.breakpoints.only('xl')} {
            font-size: 41px;
        }

        ${theme.breakpoints.between('md', 'lg')} {
            font-size: 35px;
        }

        ${theme.breakpoints.down('sm')} {
            font-size: 30px;
        }

        ${shouldDiscount && css`
            font-weight: ${theme.fonts.weights.bold};
        `}
    `;

    const taxStyle = css`
        left: 0.7rem;
        bottom: 12px;
        font-size: 18px;
        font-weight: ${theme.fonts.weights.light};

        ${!isTaxUnderPrice && css`
            position: absolute;
        `}
        
        ${theme.breakpoints.between('md', 'xl')} {
            bottom: 9px;
            font-size: 14px;
        }

        ${theme.breakpoints.down('sm')} {
            bottom: 7px;
            font-size: 12px;
        }
    `;

    return (
        <>
            <div css={containerStyle} className={className}>
                <div css={relativeContainerStyle}>
                    {shouldDiscount && (
                        <ArrowBox css={saleIcon} />
                    )}
                </div>
                <div css={relativeContainerStyle}>
                    {shouldDiscount && (
                        <p css={slashedPriceStyle}>
                            {localeCurrency(price, currency)}
                        </p>
                    )}
                    <p css={priceStyle}>
                        {localeCurrency(shouldDiscount ? discountPrice : price, currency)}
                    </p>
                </div>
                {!appState.globalCurrencyOptions.isGlobal && !isTaxUnderPrice && (
                    <div css={relativeContainerStyle}>
                        <p css={taxStyle}>
                            <ContentCombine>
                                ({taxLabelWithTaxable(isTaxable, appState.countryCode.taxLabel)})
                            </ContentCombine>
                        </p>
                    </div>
                )}
            </div>
            {!appState.globalCurrencyOptions.isGlobal && isTaxUnderPrice && (
                <p css={taxStyle}>
                    <ContentCombine>
                        ({taxLabelWithTaxable(isTaxable, appState.countryCode.taxLabel)})
                    </ContentCombine>
                </p>
            )}
        </>
    );
};

export default ItemPriceBox;
